// Providers (F-HQ-38, spec/14 §15): the external services this deployment is wired to — email, SMS, WhatsApp, IGDB,
// SteamGridDB, payments, object storage, realtime — whether each is configured, what it is (host, bucket, driver:
// never a credential), and the last check. **Test** runs the provider's side-effect-free check (SMTP connects and
// authenticates without sending; IGDB and SteamGridDB run one small search; storage heads a key; the realtime bus
// answers a read), records the result and writes one platform audit row. SMS, WhatsApp and payments have no safe
// check, so they show whether they are configured and no Test button. Part of Platform settings, `hq:settings`.
//
//   GET  /v1/hq/providers                    every provider and its last check     hq:settings
//   POST /v1/hq/providers/{providerId}/test  run the check now                     hq:settings

import type { HqProvider, HqProviderId } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, InfoTip, Table, useToast, type BadgeTone, type TableColumn } from '@clavian/ui-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { useApi } from '../api/client';
import { LoadFailure } from '../shell/LoadFailure';
import { Section, TableSkeleton } from '../ui';
import { RequirePlatformPermission } from './access';
import { PlatformSettingsHeader } from './PlatformSettings';
import { HQ_SETTINGS } from './sections';
import { useProblem } from '../api/use-problem';

const providerKeys = { all: ['hq', 'providers'] as const };

const STATUS_TONE: Readonly<Record<HqProvider['status'], BadgeTone>> = {
  not_configured: 'neutral',
  configured: 'info',
  reachable: 'ok',
  unreachable: 'danger',
};

function useProviders() {
  const api = useApi();
  return useQuery({ queryKey: providerKeys.all, queryFn: async (): Promise<readonly HqProvider[]> => (await api.listHqProviders()).data });
}

function useTestProvider() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<HqProvider, unknown, HqProviderId>({
    mutationFn: (providerId) => api.testHqProvider({ path: { providerId } }),
    onSuccess: (updated) => {
      queryClient.setQueryData<readonly HqProvider[]>(providerKeys.all, (current) => current?.map((one) => (one.id === updated.id ? updated : one)));
    },
  });
}

export function ProvidersPage(): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <RequirePlatformPermission permission={HQ_SETTINGS}>
      <div className="console-page">
        <h1 className="console-page__title">{t('settings.title')}</h1>
        <PlatformSettingsHeader current="providers" />
        <Providers />
      </div>
    </RequirePlatformPermission>
  );
}

function Providers(): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const format = useVenueFormatter();
  const providers = useProviders();
  const test = useTestProvider();
  const toast = useToast();

  const run = (provider: HqProvider) => {
    test.mutate(provider.id, {
      onSuccess: (updated) =>
        toast.show({
          tone: updated.status === 'reachable' ? 'ok' : 'danger',
          title: t(updated.status === 'reachable' ? 'providers.test.passed' : 'providers.test.failed', { name: updated.name }),
        }),
      onError: (error) => toast.show({ tone: 'danger', title: problems.text(error, { fallback: t('providers.test.error', { name: provider.name }) }) }),
    });
  };

  const columns: readonly TableColumn<HqProvider>[] = [
    {
      key: 'provider',
      header: t('providers.column.provider'),
      cell: (provider) => (
        <span>
          <strong>{provider.name}</strong>
          <br />
          <span className="console-muted">{provider.purpose}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('providers.column.status'),
      cell: (provider) => <Badge tone={STATUS_TONE[provider.status]}>{t(`providers.status.${provider.status}`)}</Badge>,
    },
    {
      key: 'configuration',
      header: t('providers.column.configuration'),
      cell: (provider) =>
        provider.details.length === 0 && provider.driver === null ? (
          <span className="console-muted">{t('providers.none')}</span>
        ) : (
          <span>
            {provider.driver === null ? null : <span className="console-num">{provider.driver}</span>}
            {provider.details.map((detail) => (
              <span key={detail.label}>
                <br />
                <span className="console-muted">{detail.label}: </span>
                <span className="console-num">{detail.value}</span>
              </span>
            ))}
          </span>
        ),
    },
    {
      key: 'lastCheck',
      header: t('providers.column.lastCheck'),
      cell: (provider) =>
        provider.lastCheckedAt === null ? (
          <span className="console-muted">{provider.testable ? t('providers.neverChecked') : t('providers.notTestable')}</span>
        ) : (
          <span>
            <span className="console-num">{format.dateTime(provider.lastCheckedAt, { date: 'medium', time: 'short' })}</span>
            {provider.lastLatencyMs === null ? null : <span className="console-muted"> · {t('providers.latency', { ms: provider.lastLatencyMs })}</span>}
            {provider.lastError === null ? null : (
              <>
                <br />
                <span className="console-muted">{provider.lastError}</span>
              </>
            )}
          </span>
        ),
    },
    {
      key: 'actions',
      header: t('providers.column.actions'),
      cell: (provider) =>
        provider.testable ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={test.isPending && test.variables === provider.id}
            aria-label={t('providers.test.label', { name: provider.name })}
            onClick={() => run(provider)}
          >
            {test.isPending && test.variables === provider.id ? t('providers.test.running') : t('providers.test.action')}
          </Button>
        ) : null,
    },
  ];

  let body: ReactNode;
  if (providers.data === undefined) {
    body = providers.isError ? <LoadFailure error={providers.error} message={t('providers.unavailable')} onRetry={() => void providers.refetch()} /> : <TableSkeleton />;
  } else {
    body = <Table caption={t('providers.caption')} captionHidden columns={columns} rows={providers.data} rowKey={(provider) => provider.id} />;
  }

  return (
    <Section title={t('providers.title')} actions={<InfoTip>{t('providers.intro')}</InfoTip>} body={providers.data === undefined ? 'padded' : 'rows'}>
      {body}
    </Section>
  );
}
